//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import Anchor from '@components/atoms/anchor/Anchor';
import { IButtonStateStyles } from '@components/atoms/button/buttonStyles';
import { ITrackableComponent } from '@utils/withTracking';

type Props = React.AnchorHTMLAttributes<HTMLAnchorElement> &
  ITrackableComponent & { styles: IButtonStateStyles };

export const ButtonAnchor = (props: Props) => {
  const { styles, children } = props;

  return (
    <StyledAnchor {...props}>
      <ButtonContainer styles={styles}>
        <ButtonText>{children}</ButtonText>
      </ButtonContainer>
    </StyledAnchor>
  );
};

const getStateStyles = (
  state: 'standard' | 'hover' | 'active' | 'disabled',
  styles: IButtonStateStyles,
) => {
  return `
    color: ${styles[state].color};
    border-color: ${styles[state].borderColor};
    background-color: ${styles[state].backgroundColor};
    box-shadow: inset 0 0 0 1.5px ${styles[state].borderColor};
  `;
};

const ButtonContainer = styled.div<{ styles: IButtonStateStyles }>(
  ({ theme, styles }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${theme.spacings[5]};
  cursor: pointer;
  padding-top: ${theme.spacings[2]};
  padding-bottom: ${theme.spacings[2]};
  padding-left: ${theme.spacings[3]};
  padding-right: ${theme.spacings[3]};
  border-radius: ${theme.borderRadii[4]};
  font-weight: ${theme.fontWeights.semiBold};

  ${getStateStyles('standard', styles)}

  &&:hover {
    ${getStateStyles('hover', styles)}
  }

  &&:active {
    ${getStateStyles('active', styles)}
  }
`,
);

const ButtonText = styled.div`
  position: relative;
  top: calc(1em * -0.1);
`;

const StyledAnchor = styled(Anchor)`
  && {
    text-decoration: none;
  }

  &&:hover {
    text-decoration: none;
  }
`;
